import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Formik } from 'formik';
import LazyLoad from 'react-lazyload';
import * as Yup from 'yup';
import uniq from 'lodash/uniq';
import classNames from 'classnames';

import SquareModal from '../_composedBlocks/SquareModal';
import FindProviders from '../../FindProviders';
import ContactUsCTA from '../../ContactUsCTA';
import { WhatIfCard, ProviderCard, NoResultsCard } from '../../SelectProvider/ProviderCards';

import whiteChevron from '../../../public/images/icons/white-chevron-right.svg';
import chevronDown from '../../../public/images/chevron-down-everdays-navy.svg';

import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';
import { providersOperations } from '@evdy/web-redux/dist/lib/webPurchase/providers';

import styles from './EditProviderModal.module.scss';

const { saveFuneralHomeToUser } = authUserOperations;
const { fetchProviders } = providersOperations;

const {
  'edit-provider-modal-body': editProviderModalBodyClass,
  'footer-section': footerSectionClass,
  'update-button': updateButtonClass,
  'cancel-button': cancelButtonClass,
  'show-more-family': showMoreFamilyProvidersClass
} = styles;

const EditProviderModal = ({
  closeModal,
  modalVisible,
  userData,
  providers,
  packageInfo,
  stateWarning
}) => {
  const dispatch = useDispatch();
  const [familyProvidersToShow, setFamilyProvidersToShow] = useState(1);

  const handleUpdate = values => {
    const funeralHome = values?.funeralHome;
    dispatch(saveFuneralHomeToUser({ funeralHome }));
  };

  const {
    _id: userId,
    address: { zip = '' } = {},
    preneed: {
      packageId: selectedPackageId,
      funeralHome: { _id: selectedHomeId, address: homeAddress } = {},
      preSelectedCompany
    } = {}
  } = userData ?? {};

  const servedFamilyProviders = useMemo(
    () => providers.filter(({ servedFamily }) => !!servedFamily),
    [providers]
  );

  const currentPackage = packageInfo.find(({ packageId }) => packageId === selectedPackageId) ?? {};
  const { colors: { color1: headerColor, color2: fillColor } = {}, packageId = '' } =
    currentPackage;

  const providersInRange = useMemo(() => {
    // filter out dupes from servedFamilyProvidersArray (remove filter when kb fixes end point/when we merge dev into project branch)
    // sort remaining providers by average rating
    const servedFamilyHomeIds = uniq(servedFamilyProviders.map(({ _id }) => _id.toString()));
    return providers
      .filter(({ servedFamily, _id }) => {
        if (!!servedFamily) return false;
        if (servedFamilyHomeIds.includes(_id.toString())) return false;
        return true;
      })
      .slice(0)
      .sort((a, b) => (a?.ratingsInfo?.average < b?.ratingsInfo?.average ? 1 : -1));
  }, [providers, servedFamilyProviders]);

  // prefetch homes near selected home on load
  useEffect(() => {
    dispatch(
      fetchProviders({
        searchTerm: !!preSelectedCompany ? '' : homeAddress?.zip,
        isAuthedUser: !!userId
      })
    );
  }, []);

  const initialValues = {
    funeralHome: !!selectedHomeId ? selectedHomeId : ''
  };
  const validationSchema = Yup.object().shape({
    funeralHome: Yup.string().required()
  });

  return (
    <Formik
      onSubmit={handleUpdate}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
    >
      {({ values, isValid, handleSubmit }) => (
        <SquareModal
          {...{ closeModal, modalVisible }}
          renderFooterContent={() => (
            <div className={footerSectionClass}>
              <button className={cancelButtonClass} onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className={updateButtonClass} onClick={handleSubmit}>
                Update Provider <img src={whiteChevron} alt="right chev" />
              </button>
            </div>
          )}
        >
          <div className={editProviderModalBodyClass}>
            <Form
              onKeyDown={e => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              className={classNames({ 'search-bottom': !!preSelectedCompany })}
            >
              {!!servedFamilyProviders.length
                ? servedFamilyProviders
                    .slice(0, familyProvidersToShow)
                    .map(provider => (
                      <ProviderCard
                        {...{ provider, headerColor, fillColor, packageId, stateWarning }}
                        fieldName="funeralHome"
                        value={provider?._id}
                        key={provider?.name}
                        isChecked={values?.funeralHome === provider?._id}
                        values={values}
                        headerCopy={`served the ${provider?.servedFamily} family`}
                      />
                    ))
                : null}
              {servedFamilyProviders.length > 1 &&
                servedFamilyProviders.length !== familyProvidersToShow && (
                  <div
                    className={showMoreFamilyProvidersClass}
                    onClick={() => setFamilyProvidersToShow(servedFamilyProviders.length)}
                  >
                    {`Show ${
                      servedFamilyProviders.length - 1
                    } other homes people you know have used`}
                    <img src={chevronDown} alt="chev-down" />
                  </div>
                )}
              {!preSelectedCompany ? (
                <FindProviders
                  initialFormValue={zip}
                  isAuthedUser={!!userId}
                  isPreSelectedCompany={!!preSelectedCompany}
                />
              ) : null}
              {!!providersInRange.length ? (
                providersInRange.map((provider, idx) => (
                  <
                    // comment out lazy load for the moment to fix issue where no homes show when the modal
                    // is opened until the modal container is scrolled
                    // LazyLoad
                    // scrollContainer={`.${editProviderModalBodyClass}`}
                    // height={150}
                    // key={provider?._id}
                    // offset={[-100, 100]}
                  >
                    {provider !== 'WhatIfCard' ? (
                      <ProviderCard
                        {...{ provider, headerColor, fillColor, idx, packageId, stateWarning }}
                        fieldName="funeralHome"
                        value={provider?._id}
                        key={provider?.name}
                        isChecked={values?.funeralHome === provider?._id}
                        values={values}
                        headerCopy={idx === 0 ? 'most loved near you' : ''}
                      />
                    ) : (
                      <WhatIfCard
                        titleCopy="What if I change my mind?"
                        bodyCopy="You can feel confident making a choice today, because even if you change your mind or move somewhere else down the road, our care team will be here to help you switch to a differen provider"
                      />
                    )}
                  </
                    // LazyLoad
                  >
                ))
              ) : (
                <NoResultsCard />
              )}
              {!!preSelectedCompany ? (
                <FindProviders
                  initialFormValue={zip}
                  isAuthedUser={!!userId}
                  isPreSelectedCompany={!!preSelectedCompany}
                />
              ) : null}
            </Form>
            <ContactUsCTA />
          </div>
        </SquareModal>
      )}
    </Formik>
  );
};

export default EditProviderModal;
